import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["Goalkeepers", "Defenders", "Midfielders", "Forwards"];

const categoryOf = (position = "") => {
  const p = position.toLowerCase();
  if (p.includes("keeper")) return "Goalkeepers";
  if (p.includes("defen")) return "Defenders";
  if (p.includes("midfield")) return "Midfielders";
  if (p.includes("forward") || p.includes("striker") || p.includes("wing")) return "Forwards";
  return null;
};

export default function DeptClients() {
  const { dept } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/clients", { params: { department: dept.slug } }).then((r) => setClients(r.data));
  }, [dept.slug]);

  const isFootball = dept.slug.includes("football");
  const q = query.trim().toLowerCase();
  const matches = clients.filter(
    (c) =>
      (!q || c.name.toLowerCase().includes(q) || (c.team || "").toLowerCase().includes(q)) &&
      (filter === "All" || !isFootball || categoryOf(c.position) === filter)
  );

  const groups = isFootball
    ? CATEGORIES.map((cat) => ({
        cat,
        players: matches.filter((c) => categoryOf(c.position) === cat),
      })).filter((g) => g.players.length > 0)
    : null;
  const ungrouped = isFootball ? matches.filter((c) => !categoryOf(c.position)) : [];
  if (groups && ungrouped.length > 0 && filter === "All") groups.push({ cat: "Squad", players: ungrouped });

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-14 pt-20 md:pt-28 pb-28">
      <header className="text-center mb-16 md:mb-20">
        <h1 className="font-display uppercase leading-[0.9] mt-5 text-5xl md:text-[7vw]">
          <span className="block text-white">Our</span>
          <span className="block" style={{ color: dept.accent }}>
            Clients.
          </span>
        </h1>
        <p className="font-body text-sm md:text-base text-white/65 mt-7 max-w-full mx-auto md:whitespace-nowrap">
          The athletes we represent — internationals, leaders, and the next generation defining the modern game.
        </p>
      </header>

      {/* Search + position filter */}
      <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-center gap-5">
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or club…"
            data-testid="client-search-input"
            className="w-full bg-[#0f0f0f] border border-white/15 text-white font-body text-sm pl-11 pr-10 py-3.5 placeholder:text-white/35 focus:outline-none transition-colors"
            style={{ caretColor: dept.accent }}
            onFocus={(e) => (e.target.style.borderColor = dept.accent)}
            onBlur={(e) => (e.target.style.borderColor = "")}
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="client-search-clear"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {isFootball && (
          <div className="flex flex-wrap gap-2" data-testid="position-filter">
            {["All", ...CATEGORIES].map((cat) => {
              const active = filter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  data-testid={`position-filter-${cat.toLowerCase()}`}
                  className="text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors duration-200"
                  style={
                    active
                      ? { backgroundColor: dept.accent, borderColor: dept.accent, color: "#000", fontWeight: 700 }
                      : { borderColor: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.6)" }
                  }
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {matches.length === 0 && (
        <p className="font-body text-white/50 text-center py-16" data-testid="client-no-results">
          No players found{q ? ` for “${query.trim()}”` : ""}.
        </p>
      )}

      {groups ? (
        <div className="space-y-16 md:space-y-24" data-testid="client-grid">
          {groups.map((g) => (
            <section key={g.cat} data-testid={`client-group-${g.cat.toLowerCase()}`}>
              <div className="flex items-center gap-4 mb-8 md:mb-10">
                <h2
                  className="font-display uppercase text-3xl md:text-5xl leading-none"
                  style={{ color: dept.accent }}
                >
                  {g.cat}
                </h2>
                <div className="flex-1 h-px bg-white/15" />
                <span className="csg-label text-white/50">{g.players.length}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {g.players.map((c) => (
                  <ClientCard key={c.id} c={c} accent={dept.accent} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="client-grid">
          {matches.map((c) => (
            <ClientCard key={c.id} c={c} accent={dept.accent} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClientCard({ c, accent }) {
  return (
    <article
            data-testid={`client-card-${c.id}`}
            className="group relative overflow-hidden aspect-[3/4] bg-[#0a0a0a]"
          >
            <img
              src={c.photo_url}
              alt={c.name}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.06] transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

            {/* corner triangle accent on hover */}
            <div
              className="absolute top-0 right-0 w-0 h-0 transition-all duration-300 opacity-0 group-hover:opacity-100"
              style={{
                borderTop: `40px solid ${accent}`,
                borderLeft: "40px solid transparent",
              }}
            />

            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
              <div className="csg-label" style={{ color: accent }}>
                {c.position}
                {c.nationality ? ` · ${c.nationality}` : ""}
              </div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mt-2 leading-[0.95] md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
                {c.name}
              </h3>
              {c.team && (
                <p className="font-body text-sm text-white/65 mt-1">{c.team}</p>
              )}
              {c.achievements && c.achievements.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {c.achievements.slice(0, 2).map((a, i) => (
                    <span
                      key={i}
                      className="text-[10px] tracking-[0.18em] uppercase px-2 py-1 border border-white/20 text-white/70"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </article>
  );
}
